import type { PoiIconName } from './poiIcons.js';

// Every leaf names a bundled icon, so a mistyped or removed one is a build
// error instead of a silently missing icon. Structurally a `Node`, which is
// what the generic tag resolver takes.
interface IconNode {
  [key: string]: IconNode | PoiIconName;
}

// hack to have only single icon for churches...
const buildingMapping: IconNode = {
  '*': 'building',
  hut: 'hut',
  church: 'church',
  chapel: 'church',
  farm: 'farm',
  parking: 'parking',
  warehouse: 'warehouse',
};

export const osmTagToIconMapping: IconNode = {
  aerialway: {
    '*': 'aerialway',
  },
  aeroway: {
    aerodrome: 'aerodrome',
    helipad: 'heliport',
    terminal: 'terminal',
  },
  amenity: {
    animal_shelter: 'animal_shelter',
    atm: 'atm',
    arts_centre: 'arts_centre',
    bank: 'bank',
    bar: 'bar',
    bbq: 'bbq',
    bench: 'bench',
    bicycle_parking: 'bicycle_parking',
    bicycle_repair_station: 'bicycle_repair_station',
    bicycle_rental: 'rental_bicycle',
    biergarten: 'biergarten',
    boat_rental: 'boat_rental',
    bureau_de_change: 'bureau_de_change',
    bus_station: 'bus_station',
    cafe: 'cafe',
    car_rental: 'rental_car',
    car_wash: 'car_wash',
    casino: 'casino',
    charging_station: 'charging_station',
    cinema: 'cinema',
    clinic: 'doctors',
    college: 'college',
    community_centre: 'community_centre',
    courthouse: 'courthouse',
    dentist: 'dentist',
    doctors: 'doctors',
    drinking_water: 'drinking_water',
    embassy: 'embassy',
    emergency_phone: 'emergency_phone',
    excrement_bags: 'excrement_bags',
    fast_food: 'fast_food',
    feeding_place: 'manger',
    game_feeding: 'manger',
    ferry: 'ferry',
    fire_station: 'fire_station',
    fountain: 'fountain',
    fuel: 'fuel',
    grave_yard: 'cemetery',
    hospital: 'hospital',
    hunting_stand: 'hunting_stand',
    ice_cream: 'ice_cream',
    internet_cafe: 'internet_cafe',
    kindergarten: 'nursery',
    library: 'library',
    motorcycle_parking: 'motorcycle_parking',
    nightclub: 'nightclub',
    parking: 'parking',
    parking_entrance: 'parking',
    parking_space: 'parking',
    pharmacy: 'pharmacy',
    place_of_worship: {
      '*': 'place_of_worship',
      building: buildingMapping,
    },
    police: 'police',
    post_box: 'post_box',
    post_office: 'post_office',
    prison: 'prison',
    pub: 'pub',
    public_bath: 'public_bath',
    public_bookcase: 'public_bookcase',
    ranger_station: 'ranger-station',
    recycling: 'recycling',
    restaurant: 'restaurant',
    school: 'school',
    shelter: {
      '*': 'shelter',
      shelter_type: {
        basic_hut: 'basic_hut',
        lean_to: 'lean_to',
        picnic_shelter: 'picnic_shelter',
        public_transport: 'public_transport',
        weather_shelter: 'weather_shelter',
      },
    },
    shower: 'shower',
    ski_rental: 'skiing_downhill',
    social_facility: 'social_facility',
    taxi: 'taxi',
    telephone: 'telephone',
    theatre: 'theatre',
    toilets: 'toilets',
    townhall: 'townhall',
    university: 'university',
    vehicle_inspection: 'vehicle_inspection',
    vending_machine: {
      '*': 'vending_machine',
      vending: {
        parking_tickets: 'parking_tickets',
        public_transport_tickets: 'public_transport_tickets',
      },
    },
    veterinary: 'veterinary',
    waste_basket: 'waste_basket',
    waste_disposal: 'waste_disposal',
    watering_place: 'watering_place',
  },
  barrier: {
    cycle_barrier: 'cycle_barrier',
    entrance: 'entrance',
    fence: 'fence',
    gate: 'gate',
    kissing_gate: 'kissing_gate',
    lift_gate: 'lift_gate',
    motorcycle_barrier: 'motorcycle_barrier',
    stile: 'stile',
    swing_gate: 'lift_gate',
    toll_booth: 'toll_booth',
    cattle_grid: 'cattle_grid',
  },
  bridge: {
    '*': 'bridge',
  },
  boundary: {
    administrative: {
      admin_level: {
        6: 'city',
        9: 'village',
      },
    },
  },
  emergency: {
    defibrillator: 'defibrillator',
    phone: 'emergency_phone',
  },
  entrance: {
    '*': 'entrance',
    // no: no_entrance, // TODO
  },
  ford: {
    yes: 'ford',
  },
  highway: {
    bus_stop: 'bus_stop',
    elevator: 'elevator',
    ford: 'ford',
    rest_area: 'highway-rest-area',
    steps: 'stairs',
    traffic_signals: 'traffic_signals',
  },
  historic: {
    archaeological_site: 'archaeological_site',
    boundary_stone: 'boundary_stone',
    castle: {
      '*': 'castle',
      castle_type: {
        fortress: 'fortress',
        palace: 'palace',
      },
    },
    city_gate: 'city_gate',
    fort: 'fort',
    manor: 'manor',
    memorial: {
      '*': 'memorial',
      memorial: {
        bust: 'bust',
        plaque: 'plaque',
        statue: 'statue',
        stone: 'memorial_stone',
      },
    },
    mine: 'mine',
    mine_shaft: 'mine',
    monument: 'monument',
    obelisk: 'obelisk',
    wayside_cross: 'cross',
    wayside_shrine: 'wayside_shrine',
  },
  landuse: {
    cemetery: 'cemetery',
    commercial: 'commercial',
    construction: 'construction',
    farmland: 'farm',
    farmyard: 'farm',
    industrial: {
      '*': 'industry',
      industrial: 'mine',
    },
    logging: 'logging',
    residential: 'residential-community',
  },
  leisure: {
    beach_resort: 'beach_resort',
    bird_hide: 'bird_hide',
    bowling_alley: 'bowling_alley',
    dog_park: 'dog_park',
    firepit: 'firepit',
    fishing: 'fishing',
    fitness_centre: 'fitness_centre',
    fitness_station: 'fitness_station',
    horse_riding: 'horse_riding',
    golf_course: 'golf',
    miniature_golf: 'miniature_golf',
    outdoor_seating: 'outdoor_seating',
    park: 'park',
    picnic_table: 'picnic_table',
    pitch: 'pitch',
    playground: 'playground',
    sauna: 'sauna',
    slipway: 'slipway',
    stadium: 'stadium',
    swimming_pool: 'water_park',
    swimming_area: 'swimming_outdoor',
    track: {
      '*': 'racetrack',
      sport: {
        cycling: 'racetrack-cycling',
        equestrian: 'racetrack-horse',
        horse_racing: 'racetrack-horse',
      },
    },
    water_park: 'water_park',
  },
  man_made: {
    adit: 'mine',
    antenna: 'antenna',
    apiary: 'beehive',
    beehive: 'beehive',
    bridge: 'bridge',
    chimney: 'chimney',
    cross: 'cross',
    clearcut: 'logging',
    dyke: 'dyke',
    embankment: 'embankment',
    foresters_lodge: "forester's_lodge",
    lighthouse: 'lighthouse',
    mast: 'mast_other',
    mineshaft: 'mine',
    reservoir_covered: 'water_works',
    tower: {
      'tower:type': {
        '*': 'tower_other',
        bell_tower: 'bell_tower',
        communication: 'tower_communication',
        observation: 'tower_observation',
      },
    },
    water_tower: 'water_tower',
    water_well: 'water_well',
    water_works: 'water_works',
    watermill: 'watermill',
    windmill: 'windmill',
  },
  military: {
    bunker: 'bunker',
    danger_area: 'danger',
  },
  natural: {
    arch: 'arch',
    beach: 'beach',
    cave_entrance: 'cave_entrance',
    earth_bank: 'earth_bank',
    fell: 'fell',
    gully: 'gully',
    hot_spring: 'hot_spring',
    peak: 'peak',
    rock: 'rock',
    saddle: 'saddle',
    sinkhole: 'sinkhole',
    spring: {
      '*': 'spring',
      drinking_water: {
        no: 'not_drinking_spring',
        yes: 'drinking_spring',
      },
      refitted: {
        yes: {
          '*': 'refitted_spring',
          drinking_water: {
            no: 'refitted_not_drinking_spring',
            yes: 'refitted_drinking_spring',
          },
        },
      },
    },
    stone: 'stone',
    tree: {
      '*': 'tree2',
      denotation: {
        natural_monument: 'tree',
      },
      protected: {
        yes: 'tree_protected',
      },
    },
    volcano: 'volcano',
    wetland: 'wetland',
  },
  obstacle: {
    '*': 'obstacle',
    vegetation: 'obstacle_vegetation',
    dense_vegetation: 'obstacle_vegetation',
    fallen_tree: 'obstacle_tree',
    fallen_trees: 'obstacle_tree',
    'fallen trees': 'obstacle_tree',
    deadwood: 'obstacle_tree',
    branches: 'obstacle_tree',
    tree: 'obstacle_tree',
    trees: 'obstacle_tree',
    root: 'obstacle_tree',
  },
  place: {
    city: 'city',
    town: 'town',
    farm: 'farm',
    village: 'village',
  },
  railway: {
    '*': 'rail',
    light_rail: 'rail-light',
    subway: 'rail-metro',
  },
  route: {
    horse: 'horse_riding',
    ski: 'skiing_crosscountry',
  },
  ruins: {
    '*': 'ruins',
  },
  shop: {
    '*': 'shop',
    alcohol: 'alcohol',
    art: 'art',
    bag: 'bag',
    bakery: 'bakery',
    beauty: 'beauty',
    bed: 'bed',
    beverages: 'beverages',
    bicycle: 'bicycle',
    bookmaker: 'bookmaker',
    books: 'book',
    butcher: 'butcher',
    car_parts: 'car_parts',
    carpet: 'carpet',
    car_repair: 'car_repair',
    car: 'car',
    charity: 'charity',
    chemist: 'chemist',
    clothes: 'clothes',
    coffee: 'coffee',
    computer: 'computer',
    confectionery: 'confectionery',
    convenience: 'convenience',
    copyshop: 'copyshop',
    dairy: 'dairy',
    deli: 'deli',
    department_store: 'department_store',
    diy: 'diy',
    electronics: 'electronics',
    fabric: 'fabric',
    farm: 'greengrocer',
    florist: 'florist',
    furniture: 'furniture',
    garden_centre: 'garden_centre',
    gift: 'gift',
    greengrocer: 'greengrocer',
    hairdresser: 'hairdresser',
    hardware: 'hardware',
    hifi: 'hifi',
    houseware: 'houseware',
    ice_cream: 'ice_cream',
    interior_decoration: 'interior_decoration',
    jewelry: 'jewelry',
    laundry: 'laundry',
    marketplace: 'marketplace',
    massage: 'massage',
    medical_supply: 'medical_supply',
    mobile_phone: 'mobile_phone',
    musical_instrument: 'musical_instrument',
    music: 'music',
    newsagent: 'newsagent',
    optician: 'optician',
    outdoor: 'outdoor',
    paint: 'paint',
    perfumery: 'perfumery',
    pet: 'pet',
    photo: 'photo',
    seafood: 'seafood',
    second_hand: 'second_hand',
    shoes: 'shoes',
    sports: 'sports',
    stationery: 'stationery',
    supermarket: 'supermarket',
    tea: 'tea',
    ticket: 'ticket',
    tobacco: 'tobacco',
    toys: 'toys',
    trade: 'trade',
    travel_agency: 'travel_agency',
    tyres: 'tyres',
    variety_store: 'variety_store',
    video_games: 'video_games',
    video: 'video',
    watches: 'watch',
  },
  sport: {
    '8pin': 'bowling_alley',
    '9pin': 'bowling_alley',
    '10pin': 'bowling_alley',
    archery: 'archery',
    american_football: 'american_football',
    baseball: 'baseball',
    basketball: 'basketball',
    beachvolleyball: 'volleyball',
    bicycle: 'bicycle',
    bowling: 'bowling_alley',
    candlepin: 'bowling_alley',
    climbing: 'hillclimbing',
    cricket: 'cricket',
    cycling: 'bicycle',
    diving: 'diving',
    equestrian: 'horse_riding',
    free_flying: 'free_flying',
    golf: 'golf',
    gym: 'gym',
    gymnastics: 'gymnasium',
    horse_racing: 'horse_riding',
    horse_riding: 'horse_riding',
    ice_skating: 'iceskating',
    motor: 'motorracing',
    skateboard: 'skateboard',
    skating: 'iceskating',
    ski_jumping: 'skiing_downhill',
    skiing: 'skiing_downhill',
    soccer: 'soccer',
    swimming: 'swimming_outdoor',
    table_tennis: 'table-tennis',
    tennis: 'tennis',
    volleyball: 'volleyball',
  },
  toll: {
    '*': 'toll',
  },
  tourism: {
    alpine_hut: 'alpine_hut',
    apartment: 'apartment',
    artwork: {
      '*': 'artwork',
      artwork_type: {
        bust: 'bust',
      },
    },
    attraction: 'attraction',
    camp_site: 'camp_site',
    chalet: 'chalet',
    guest_house: 'guest_house',
    hostel: 'hostel',
    hotel: 'hotel',
    information: {
      '*': 'information',
      information: {
        board: 'board',
        guidepost: 'guidepost',
        map: 'map',
        office: 'office',
      },
    },
    motel: 'motel',
    museum: 'museum',
    picnic_site: 'picnic_table',
    viewpoint: 'viewpoint',
    wilderness_hut: 'wilderness_hut',
    zoo: 'zoo',
  },
  tunnel: {
    '*': 'tunnel',
    // no: '', TODO
  },
  waterway: {
    dam: 'dam',
    waterfall: 'waterfall',
  },

  // NOTE keep at the end
  building: buildingMapping,
  fixme: {
    '*': 'fixme',
  },
};
